
import React, { FormEventHandler } from 'react';
import {
	InitialSetupArguments,
	LoginArguments,
	TwoFactorConfiguration,
	TwoFactorMethods,
} from 'Services/TwoFactor/Common';
import useAsync from 'Hooks/useAsync';
import Spinner from 'Views/Components/Spinner/Spinner';
import { useLocalStore } from 'mobx-react';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { Button } from 'Views/Components/Button/Button';
import { validate2faCode } from 'Services/Api/AuthorizationService';
import QRCode from 'qrcode.react';
import { APPLICATION_NAME } from 'Constants';
import alertToast from 'Util/ToastifyUtils';
import { Checkbox } from 'Views/Components/Checkbox/Checkbox';
import { logout } from 'Util/NavigationUtils';
import { useLocation } from 'react-router-dom';
import { ButtonGroup } from 'Views/Components/Button/ButtonGroup';


export const AuthenticatorTokenType = 'Authenticator';
const GoogleAuthenticatorLink = 'https://support.google.com/accounts/answer/1066447';
const AuthyLink = 'https://authy.com/';

export function buildOtpUrl(application: string, userName: string, secret: string, issuer: string): string {
	const escapedApp = encodeURIComponent(application);
	const escapedUser = encodeURIComponent(userName);
	const escapedIssuer = encodeURIComponent(issuer);
	return `otpauth://totp/${escapedApp}:${escapedUser}?secret=${secret}&issuer=${escapedIssuer}&digits=6`;
}

function formatCode(code: string): string {
	let result = '';
	for (let i = 0; i < code.length; i += 4) {
		// Adding a trailing space is less confusing with concat compared to template strings
		// eslint-disable-next-line prefer-template
		result += code.substr(i, 4) + ' ';
	}
	return result.trim();
}

export interface AuthenticatorInitialSetupProps extends InitialSetupArguments {

}

export function AuthenticatorInitialSetup({
	userName,
	loggedInUserName,
	close,
	configurePromise,
}: AuthenticatorInitialSetupProps) {
	const location = useLocation();
	const response = useAsync(() => configurePromise, []);
	const inputData = useLocalStore(() => ({
		code: '',
	}));

	const onSubmit: FormEventHandler = e => {
		e.preventDefault();
		validate2faCode({
			token: inputData.code,
			method: AuthenticatorTokenType,
		})
			.then(() => {
				alertToast('Successfully enabled two factor authentication', 'success');
				close();
			})
			.catch(error => {
				console.error(error);
				alertToast('Invalid authentication code. Please try again.', 'error');
			});
	};

	const onCloseAndLogout = () => {
		logout(location.pathname);
	};

	if (response.type === 'loading') {
		return <Spinner />;
	}

	if (response.type === 'error') {
		console.error(response.error);
		return (
			<div>
				<p>There was an error configuring the authenticator application.</p>
				<Button onClick={() => close(true)}>Close</Button>
			</div>
		);
	}

	if (response.data.method === AuthenticatorTokenType) {
		const formattedCode = <pre>{formatCode(response.data.code)}</pre>;
		const qrCode = <QRCode value={buildOtpUrl(APPLICATION_NAME, userName, response.data.code, APPLICATION_NAME)} />;

		if (userName === loggedInUserName) {
			return (
				<div>
					<p>To use this code you have to go through the following steps</p>
					<ol>
						<li>
							Download a two factor authenticator app like
							<a href={GoogleAuthenticatorLink}>Google Authenticator</a> or
							<a href={AuthyLink}>Authy</a>.
						</li>
						<li>
							<p>
								Scan the QR code or enter the following key into your two factor authenticator app.
								Spaces and casing do not matter.
							</p>
							{formattedCode}
							{qrCode}
						</li>
						<li>
							<p>
								Once you have scanned the QR code or input the text above, the your two factor
								authentication app will provide you with a unique code. Enter the code in the
								confirmation box below.
							</p>
							<p>
								If you do not enter the code you will be logged out from the application.
							</p>
							<form onSubmit={onSubmit}>
								<TextField model={inputData} modelProperty="code" label="Authenticator Code" />
								<ButtonGroup>
									<Button type="submit">Submit</Button>
									<Button onClick={onCloseAndLogout}>Close and logout</Button>
								</ButtonGroup>
							</form>
						</li>
					</ol>
				</div>
			);
		}

		return (
			<div>
				<p>Successfully set up two factor authentication for {userName}</p>
				<ol>
					<li>
						<p>
							Send the QR code or authenticator key to the {userName} so they
							can log into their account.
						</p>
						{formattedCode}
						{qrCode}
					</li>
					<li>
						Ask them to add the authenticator key or QR code to an app like{' '}
						<a href={GoogleAuthenticatorLink}>Google Authenticator</a> or{' '}
						<a href={AuthyLink}>Authy</a>.
					</li>
				</ol>
				<Button onClick={() => close()}>Close</Button>
			</div>
		);
	}

	console.error('Invalid response for Authenticator configuration', response);
	return <div>Unable to configure</div>;
}

export interface AuthenticatorLoginPageProps extends LoginArguments {

}

export function AuthenticatorLoginPage({
	onTwoFactorSuccess,
	rememberMe,
}: AuthenticatorLoginPageProps) {
	const inputData = useLocalStore(() => ({
		code: '',
		rememberTwoFactor: false,
	}));

	const onSubmit: FormEventHandler = e => {
		e.preventDefault();
		validate2faCode({
			token: inputData.code,
			method: AuthenticatorTokenType,
			rememberTwoFactor: inputData.rememberTwoFactor,
			rememberMe: rememberMe,
		})
			.then(onTwoFactorSuccess)
			.catch(error => {
				alertToast(
					`Two factor error ${error}`,
					'error',
				);
			});
	};

	return (
		<div className="body-content">
			<form onSubmit={onSubmit}>
				<h2>Verify Login</h2>
				<div>
					Please get the auth code from your phone.
					Please enter that code and press continue to finish logging in.
				</div>
				<TextField model={inputData} modelProperty="code" />
				<Checkbox model={inputData} modelProperty="rememberTwoFactor" label="Skip this step for 14 days" />
				<Button type="submit">Submit</Button>
			</form>
		</div>
	);
}

export const AuthenticatorTwoFactorConfiguration: TwoFactorConfiguration = {
	onInitialSetup: args => ({
		content: <AuthenticatorInitialSetup {...args} />,
		required: true,
	}),
	onRemove: () => ({
		content: undefined,
		required: false,
	}),
	onLogin: args => ({
		content: <AuthenticatorLoginPage {...args} />,
		required: true,
	}),
};

export function configureAuthenticator2fa(methods: TwoFactorMethods) {
	methods[AuthenticatorTokenType] = AuthenticatorTwoFactorConfiguration;
}


